import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from '@Redux/type';
import { AppState } from '@Redux/store';

import { armyListUnitEditPresenter } from '@Selectors/builder/myArmyList/make/armyListUnitEditPresenter';
import Repeater from '@Utils/Repeater';
import ArmyListUnitExport from '../../export/mode/ArmyListUnitExport';
import WarningMessage from '@Selectors/builder/myArmyList/make/unit/edit/warning/WarningMessage';
import Scroll from 'react-scroll';
import { container } from '@Container';
import { TYPES } from '@Types';
import { ArmyListUnitAction } from '@Redux/actions';
import { FormattedMessage } from 'react-intl';
import { defaultMessages } from '@I18n/default';
import UnitOptionGroupComponent from '@Components/builder/unit-option-group/unit-option-group.component';
import armyListUnitSlice from '@Redux/reducers/builder/armyListUnit.slice';
import { UnitOptionGroupView } from '@Selectors/builder/myArmyList/make/view/unit-option-group.view';

const ArmyListUnitEdit: React.FC<ArmyListUnitEditProps> = ({
  armyListUnit,
  name,
  notes,
  points,
  changeName,
  size,
  hasWarning,
  warning,
  unitOptionGroups,
  changeSize,
  changeNotes,
  cancel,
  toEdit,
}) => {
  useEffect(() => {
    Scroll.scroller.scrollTo('myScrollToElement', {
      duration: 1000,
      delay: 100,
      smooth: true,
      offset: -50, // Scrolls to element + 50 pixels down the page
    });
  }, []);
  const Element = Scroll.Element;

  const allOptions = unitOptionGroups.flatMap((group) => group.options);

  return (
    <div className="m-2 form-horizontal">
      <Element name="myScrollToElement" />
      <div className="float-end">{points} pts</div>

      {hasWarning && (
        <div className="form-group">
          <ul className="ms-2">
          <Repeater data={warning} indexPrefix="warning">
            {(item: WarningMessage) => (
              <li className="highlight-red">{item.message}</li>
            )}
          </Repeater>
          </ul>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="name">Name:</label>
        <input
          type="text"
          value={name}
          name="name"
          placeholder="Unit name"
          className="form-control"
          onChange={(e) => {
            changeName(e.target.value);
          }}
        />
      </div>

      {size.canChange && (
        <div className="form-group">
          <label htmlFor="size">Size:</label>
          <input
            className="ms-1"
            type="number"
            value={size.current}
            min={size.min}
            max={size.max}
            step="1"
            onChange={(e) => {
              changeSize(e.target.value);
            }}
          />{' '}
          / {size.max} * {size.points} pts/mod.
        </div>
      )}

      <div className="row">
        <Repeater data={unitOptionGroups} indexPrefix={'unit-option-group'}>
          {
            (group: UnitOptionGroupView) => (
              <UnitOptionGroupComponent unitOptionGroup={group} allOptions={allOptions} />
            )
          }
        </Repeater>
      </div>

      <div className="form-group">
        <label htmlFor="size">Notes:</label>
        <textarea
          className="ms-1 form-control"
          value={notes}
          onChange={(e) => {
            changeNotes(e.target.value);
          }}
        />
      </div>

      <div className="text-center">
        <ArmyListUnitExport armyListUnit={armyListUnit} showPoints={false} />
      </div>

      <div className="action text-center">
        <button className="btn btn-age" type="button" onClick={() => cancel()}>
          <FormattedMessage {...defaultMessages.formCancel} />
        </button>
        <button className="btn btn-age" type="button" onClick={() => toEdit()}>
          <i className="fa fa-edit" aria-hidden="true" /> <FormattedMessage {...defaultMessages.formEdit} />
        </button>
      </div>
    </div>
  );
};

const mapStateToProps = (
  state: AppState
) => armyListUnitEditPresenter(state);

const mapDispatchToProps = (dispatch: Dispatch) => {
  const armyListUnitAction = container.get<ArmyListUnitAction>(
    TYPES.Actions.ArmyListUnitAction
  );
  return {
    changeName: (name: string) =>
      dispatch(armyListUnitSlice.actions.setName(name)),
    changeNotes: (notes: string) =>
      dispatch(armyListUnitSlice.actions.setNotes(notes)),
    changeSize: (input: string) => {
      const minInt = parseInt(input);
      dispatch(armyListUnitAction.armyListUnitSetSize(minInt));
    },
    toEdit: () => dispatch(armyListUnitAction.actionEditArmyListUnit()),
    cancel: () => dispatch(armyListUnitSlice.actions.cancel()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ArmyListUnitEdit);

type ArmyListUnitEditProps = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;
